'use client'
import { jsx, jsxs } from "react/jsx-runtime";
import external_react_default, { useState } from "react";
import { Button } from "../Button/index.js";
import { Span } from "../Text/index.js";
import { cn } from "../../utils/cn.js";
import { counterIconButtonVariants, counterVariants } from "./Counter.variants.js";
import { CounterIconButton } from "./CounterIconButton.js";
import { SlidingNumber } from "../animations/SlidingNumber/index.js";
function Counter({ iconButtonType = 'primary', incrementIconButtonProps = {}, decrementIconButtonProps = {}, ctaButtonProps = {}, emphasis = 'regular', min = 0, max = 10, cta = false, label, className = '', size, disabled = false, handleValueChange, value = min, decrementIconTooltipProps, incrementIconTooltipProps, useSlidingNumber = false }) {
    const id = external_react_default.useId();
    const showCta = cta && 0 === value;
    const [displayCta, setDisplayCta] = useState(showCta);
    const hideCta = ()=>{
        handleValueChange(++value);
        setDisplayCta(false);
    };
    const decrementValue = ()=>{
        const count = --value;
        if (count === min && cta) setDisplayCta(true);
        handleValueChange(count);
    };
    const incrementValue = ()=>{
        handleValueChange(++value);
    };
    const minusDisabled = disabled || value <= min;
    const plusDisabled = disabled || value >= max;
    const minusLabel = `Decrease ${label}${minusDisabled ? ' - disabled' : ''}`;
    const plusLabel = `Increase ${label}${plusDisabled ? ' - disabled' : ''}`;
    const addButtonType = counterIconButtonVariants[iconButtonType].add;
    const removeButtonType = counterIconButtonVariants[iconButtonType].remove;
    const { size: ctaSize = 'sm', children: ctaChildren = 'Add', iconLeft = 'add', ...restProps } = ctaButtonProps;
    const { root, ctaButton, numericText } = counterVariants({
        emphasis,
        size
    });
    const rootClasses = cn(root(), className);
    if (displayCta) return /*#__PURE__*/ jsx(Button, {
        "data-testid": "counter-cta-button",
        size: ctaSize,
        children: ctaChildren,
        onClick: hideCta,
        type: iconButtonType,
        iconLeft: iconLeft,
        emphasis: emphasis,
        disabled: disabled,
        className: ctaButton(),
        ...restProps
    });
    return /*#__PURE__*/ jsxs("div", {
        role: "group",
        "aria-label": `${label} counter controls`,
        className: rootClasses,
        "aria-valuemin": min,
        "aria-valuemax": max,
        "aria-valuenow": value,
        children: [
            /*#__PURE__*/ jsx(CounterIconButton, {
                type: "decrement",
                ariaLabel: minusLabel,
                ariaControls: id,
                onClick: decrementValue,
                tooltipProps: decrementIconTooltipProps,
                buttonType: removeButtonType,
                disabled: minusDisabled,
                emphasis: emphasis,
                iconButtonProps: decrementIconButtonProps
            }),
            useSlidingNumber ? /*#__PURE__*/ jsx("div", {
                className: "w-10 items-center justify-center whitespace-nowrap text-center",
                children: /*#__PURE__*/ jsx(SlidingNumber, {
                    value: value,
                    className: numericText(),
                    "data-testid": "counter-value"
                })
            }) : /*#__PURE__*/ jsx(Span, {
                align: "center",
                id: id,
                "aria-live": "polite",
                "aria-atomic": "true",
                className: numericText(),
                "data-testid": "counter-value",
                children: value
            }),
            /*#__PURE__*/ jsx(CounterIconButton, {
                type: "increment",
                ariaLabel: plusLabel,
                ariaControls: id,
                onClick: incrementValue,
                tooltipProps: incrementIconTooltipProps,
                buttonType: addButtonType,
                disabled: plusDisabled,
                emphasis: emphasis,
                iconButtonProps: incrementIconButtonProps
            })
        ]
    });
}
export { Counter };
